import React from "react";
import { Box, Typography } from "@mui/material";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";

const COLORS = [
  "#3592FB",
  "#9A40E0",
  "#4CAF50",
  "#FF9800",
  "#607D8B",
  "#9C27B0",
];

/* ---------- External Labels With Connector Lines ---------- */
const renderExternalLabel = ({
  cx,
  cy,
  midAngle,
  outerRadius,
  percent,
  name,
  fill,
  index,
}) => {
  const RADIAN = Math.PI / 180;
  const sin = Math.sin(-RADIAN * midAngle);
  const cos = Math.cos(-RADIAN * midAngle);

  // base line positions
  const sx = cx + outerRadius * cos;
  const sy = cy + outerRadius * sin;

  const ex = cx + (outerRadius + 28) * cos;
  const ey = cy + (outerRadius + 28) * sin;

  // vertical margin (top / bottom spacing)
  const verticalOffset = (index % 2 === 0 ? 1 : -1) * 12;

  // label position (detached + spaced)
  const lx = ex + (cos >= 0 ? 14 : -14);
  const ly = ey + verticalOffset;

  const textAnchor = cos >= 0 ? "start" : "end";

  return (
    <g>
      {/* connector line */}
      <line x1={sx} y1={sy} x2={ex} y2={ey} stroke={fill} strokeWidth={1} />

      {/* dot */}
      <circle cx={ex} cy={ey} r={3} fill={fill} />

      {/* label background */}
      <rect
        x={lx + (cos >= 0 ? 0 : -95)}
        y={ly - 11}
        width={95}
        height={22}
        rx={6}
        fill="#fff"
        opacity={0.9}
      />

      {/* label text */}
      <text
        x={lx}
        y={ly}
        textAnchor={textAnchor}
        dominantBaseline="central"
        fontSize={12}
        fill="#333"
        fontWeight={500}
      >
        {name} {(percent * 100).toFixed(1)}%
      </text>
    </g>
  );
};

function ChartRenderer({ data, totalRecord, activeFilterType }) {
  const chartFormatter = new Intl.NumberFormat("fa-IR");
  const toPersianDigits = (input) => {
    if (input === null || input === undefined) return "";

    const str = input.toString();

    // normalize Persian digits to English
    const normalized = str.replace(
      /[۰-۹]/g,
      (d) => "0123456789"["۰۱۲۳۴۵۶۷۸۹".indexOf(d)],
    );

    // find number only
    const match = normalized.match(/\d+(?:,\d{3})*(?:\.\d+)?/);
    if (!match) return str;

    const formatted = Number(match[0].replace(/,/g, "")).toLocaleString(
      "en-US",
    );

    const persianFormatted = formatted.replace(/\d/g, (d) => "۰۱۲۳۴۵۶۷۸۹"[d]);

    // replace ONLY the matched number
    return str.replace(match[0], persianFormatted);
  };

  const pieData = data?.items || data || [];
  const hasData = pieData.length > 0;
  const safeData =
    hasData ? pieData : [{ name: "بدون اطلاعات", value1: 1 }];

  const total = pieData.reduce((sum, item) => sum + item.value, 0);

  return (
    <Box
      sx={{
        display: "flex",
        gap: 2,
        alignItems: "flex-start",
        flexWrap: "nowrap",
        overflowX: { xs: "auto", md: "visible" },
      }}
    >
      {/* ---------- Pie Chart ---------- */}
      <Box
        sx={{
          minWidth: 320,
          width: { xs: 320, md: "60%" },
          height: 360,
          flexShrink: 0,
        }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={safeData}
              dataKey="value1"
              nameKey="name"
              cx="50%"
              cy="50%"
              outerRadius={100}
              innerRadius={55}
              paddingAngle={2}
              // label={pieData.length ? renderExternalLabel : false}
              labelLine={false}
            >
              {safeData.map((_, index) => (
                <Cell
                  key={index}
                  fill={pieData.length ? COLORS[index % COLORS.length] : "#ccc"}
                />
              ))}
            </Pie>
            <Tooltip
              content={({ active, payload }) => {
                if (active && payload?.length) {
                  const d = payload[0];
                  const color = hasData
                    ? COLORS[
                        pieData.findIndex((p) => p.name === d.name) %
                          COLORS.length
                      ]
                    : "#ccc";
                  return (
                    <Box
                      sx={{
                        background: "#fff",
                        padding: 1,
                        border: "1px solid #ccc",
                        borderRadius: 1,
                      }}
                    >
                      <Typography sx={{ color, fontWeight: 500 }}>
                        {toPersianDigits(d.name)}:{" "}
                        {hasData
                          ? toPersianDigits(d.value)
                          : toPersianDigits(0)}
                      </Typography>
                    </Box>
                  );
                }
                return null;
              }}
            />{" "}
          </PieChart>
        </ResponsiveContainer>
      </Box>
  <Box
        sx={{ width: "100%", display: "flex", padding: "4px", justifyContent: "center" }}
      >
      {/* ---------- Legend (UNCHANGED) ---------- */}
      <Box
        sx={{ width: "60%", display: "flex", justifyContent: "space-between", flexDirection: "column", gap: 1 }}
      >
        {activeFilterType === 1 && (
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 , textAlign: "center",
        alignItems: "center" }}>
            <Typography fontWeight="bold" flex={1} px={1}>
              نوع فروش
            </Typography>
            <Typography fontWeight="bold" flex={1} px={1}>
              درصد
            </Typography>
            <Typography fontWeight="bold" flex={1} px={1}>
              متراژ
            </Typography>
          </Box>
        )}
        {activeFilterType === 2 && (
          <Box sx={{ display: "flex", mb: 1 }}>
            <Typography fontWeight="bold" flex={1} px={1}>
              نوع
            </Typography>
            <Typography fontWeight="bold" flex={1} px={1}>
              درصد
            </Typography>
            <Typography fontWeight="bold" flex={1} px={1}>
              متراژ
            </Typography>
          </Box>
        )}

        {safeData.map((item, index) => {
          const percent = pieData.length
            ? ((item.value / total) * 100).toFixed(2)
            : 0;

          return (
            <Box
              key={index}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                textAlign: "center",
              }}
            >
              <Typography
                sx={{ flex:1, textAlign: "center" }}
                color={COLORS[index % COLORS.length]}
              >
                {item.name}
              </Typography>
              <Typography sx={{ flex:1, textAlign: "center" }}>
                {toPersianDigits(percent)}%
              </Typography>
              <Typography sx={{ flex:1, textAlign: "center" }}>
                {toPersianDigits(item?.value1 || 0)}
              </Typography>
            </Box>
          );
        })}
      </Box>
      </Box>
    </Box>
  );
}

export default ChartRenderer;
